function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined
  if (!path.includes('.')) return obj[path]
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && !Array.isArray(acc)) {
      return (acc as Record<string, unknown>)[key]
    }
    return undefined
  }, obj)
}

export function getFlexibleFieldValue(
  item: Record<string, unknown> | null | undefined,
  key: string
): unknown {
  if (!item || !key) return undefined
  const direct = getNestedValue(item, key)
  if (direct !== undefined) return direct
  if (!key.includes('.')) {
    const match = Object.keys(item).find(
      (candidate) => candidate.toLowerCase() === key.toLowerCase()
    )
    if (match) return item[match]
  }
  return undefined
}

export function getRowSummaryFieldNamesFromMeta(
  meta: { fields?: Array<{ name?: string }> } | null | undefined,
  limit = 6
): string[] {
  const fields = meta?.fields
  if (!Array.isArray(fields)) return []
  const skip = new Set(['_id', 'uid', '_created', '_updated'])
  const names: string[] = []
  for (const f of fields) {
    if (names.length >= limit) break
    const n = typeof f?.name === 'string' ? f.name : ''
    if (!n || skip.has(n)) continue
    names.push(n)
  }
  return names
}

/**
 * Short single-line summary from ordered field keys (tooltips, aria-labels, exports).
 */
export function buildRowFieldSummary(
  item: Record<string, unknown> | null | undefined,
  fieldNames: string[],
  options?: { maxParts?: number; maxLength?: number }
): string {
  const maxParts = options?.maxParts ?? 4
  const maxLength = options?.maxLength ?? 200
  if (!item || !fieldNames.length) return ''
  const parts: string[] = []
  for (const name of fieldNames) {
    if (parts.length >= maxParts) break
    const v = getFlexibleFieldValue(item, name)
    if (v === undefined || v === null) continue
    const s =
      typeof v === 'object'
        ? Array.isArray(v)
          ? v.map((x) => (x === undefined || x === null ? '' : String(x))).filter(Boolean).join(', ')
          : JSON.stringify(v)
        : String(v)
    const t = s.trim()
    if (!t) continue
    parts.push(t)
  }
  const joined = parts.join(' · ').trim()
  return joined.length > maxLength ? `${joined.slice(0, maxLength - 1)}…` : joined
}

export const ROW_FIELD_SUMMARY_COUNT = 4

const SUMMARY_SKIP_FIELD_TYPES = new Set(['image', 'file', 'instruction', 'richtext'])

function formatValueForRowSummary(val: unknown): string {
  if (val == null || val === '') return '—'
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return String(val)
  if (typeof val === 'string') {
    const t = val.trim()
    if (!t) return '—'
    return t.length > 48 ? `${t.slice(0, 48)}…` : t
  }
  if (Array.isArray(val)) return val.length ? `[${val.length}]` : '—'
  if (typeof val === 'object') return '{…}'
  return String(val)
}

export type RowSummaryFieldMeta = {
  name: string
  label?: string
  type: string
  order?: number
}

/**
 * Short line from the first N ordered scalar-ish fields (collection field meta + item).
 */
export function buildRowFieldSummaryLine(
  item: Record<string, unknown> | undefined | null,
  metaFields: RowSummaryFieldMeta[]
): string {
  if (!item) return '—'
  const withOrder = metaFields.map((f, index) => ({
    field: f,
    _order: f.order === undefined ? index : f.order,
  }))
  const sorted = [...withOrder].sort((a, b) => (a._order || 0) - (b._order || 0))
  const picked = sorted
    .map((x) => x.field)
    .filter((f) => f.name && !SUMMARY_SKIP_FIELD_TYPES.has(f.type))
    .slice(0, ROW_FIELD_SUMMARY_COUNT)
  if (picked.length === 0) {
    const u = item.uid != null ? String(item.uid) : ''
    return u || '—'
  }
  return picked
    .map((f) => {
      const label = String(f.label || f.name || f.name).trim() || f.name
      return `${label}: ${formatValueForRowSummary(item[f.name])}`
    })
    .join(' · ')
}
